import * as api from '@apis/user-event-records';
import { useAuthentication } from '@hooks/authentication';
import { UserEventRecord } from '@models/common';
import { useUserEventRecordsStore } from '@stores/userEventRecordsStore';
import { useQuery } from '@tanstack/react-query';
import { getAddressText, getChildrenText } from '@utils/dataTransform';
import { Button, Input, Table } from 'antd';
import { filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

export const UserEventRecordsPage: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const searchInputRef = useRef<any>();

  const store = useUserEventRecordsStore();

  useAuthentication();

  const userEventRecordsQuery = useQuery({
    queryKey: [`user-event-records`, JSON.stringify(store.searchQuery)],
    queryFn: () => api.searchUserEventRecords(store.searchQuery),
  });

  const userEventRecords = userEventRecordsQuery.data?.data;

  useEffect(() => {
    if (userEventRecords) {
      store.setUserEventRecordsContainer(userEventRecords);
    }
  }, [userEventRecords]);

  const TABLE_COLUMNS: ColumnsType<UserEventRecord> = [
    {
      title: 'ID',
      dataIndex: '_id',
      render: (_id) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(_id);
                toast.dark('ID를 클립보드에 복사했습니다', {
                  position: 'top-center',
                  autoClose: 3000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  bodyStyle: {
                    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                    textAlign: 'center',
                    fontFamily: 'Pretendard',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                  },
                  theme: 'dark',
                });
              }}>
              ID
            </Button>
          </div>
        );
      },
    },
    {
      title: '구분',
      width: 200,
      render: (record: UserEventRecord) => {
        return record.eventKey;
      },
    },
    {
      title: '유저 ID',
      width: 200,
      render: (record: UserEventRecord) => {
        return record.userId;
      },
    },
    {
      title: '참여자 이름',
      width: 200,
      render: (record: UserEventRecord) => {
        return record.data.parentName;
      },
    },
    {
      title: '참여자 전화번호',
      width: 200,
      render: (record: UserEventRecord) => {
        return record.data.parentPhone;
      },
    },
    {
      title: '참여자 아이',
      width: 200,
      render: (record: UserEventRecord) => {
        return getChildrenText(record.data.children);
      },
    },
    {
      title: '참여자 지역',
      width: 200,
      render: (record: UserEventRecord) => {
        return getAddressText({ availableArea: { specificAddress: record.data.parentAddress } });
      },
    },
    {
      title: '생성',
      dataIndex: 'createdAt',
      render: (createdAt) => {
        if (!createdAt) {
          return '-';
        }
        return moment(createdAt).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      title: '수정',
      dataIndex: 'updatedAt',
      render: (updatedAt) => {
        if (!updatedAt) {
          return '-';
        }
        return moment(updatedAt).format('YYYY.MM.DD HH:mm');
      },
    },
  ];
  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <div className="page-header">
        <div>이벤트 참여 관리</div>
        <Button
          onClick={async () => {
            const fileName = `igogo-user-event-records-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
            const blob = await api.getUserEventRecordsExcel(store.searchQuery);
            if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
              // IE variant
              (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
            } else {
              const url = window.URL.createObjectURL(blob.data);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', fileName);
              document.body.appendChild(link);
              link.click();
              link.parentNode && link.parentNode.removeChild(link);
            }
          }}>
          엑셀 파일 다운로드
        </Button>
      </div>
      <div style={{ display: 'flex' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div style={{ display: 'flex', gap: '12px', width: '100%', height: '52px', alignItems: 'center' }}>
            <Input
              className={filterActiveClassName(!!store.searchQuery.keyword)}
              ref={searchInputRef}
              style={{ width: '240px' }}
              value={searchKeyword}
              placeholder={'검색어 입력 후 엔터'}
              onChange={(e) => {
                setSearchKeyword(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.code === 'Enter' && searchKeyword) {
                  store.updateSearchQuery({ keyword: searchKeyword });
                }
              }}></Input>
            <div>{`검색결과: ${store.searchResultContainer.total}`}</div>

            <div style={{ display: 'flex', marginLeft: 'auto', gap: '12px', alignItems: 'center' }}>
              <Button
                onClick={() => {
                  setSearchKeyword(undefined);
                  store.resetSearchQuery();
                }}>
                검색 초기화
              </Button>
            </div>
          </div>
          <Table
            columns={TABLE_COLUMNS}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 280px)' }}
            style={{ whiteSpace: 'pre-wrap' }}
            rowKey={(record) => record?._id}
            dataSource={store.searchResultContainer.contents.filter((product) => product?._id)}
            pagination={{
              current: (store.searchResultContainer.page || 0) + 1,
              pageSize: store.searchResultContainer.size || 20,
              total: store.searchResultContainer.total,
            }}
            loading={userEventRecordsQuery.isLoading}
            onChange={(e) => {
              store.updateSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default UserEventRecordsPage;
